import React, { useState, useEffect } from "react";
import axios from "axios";
import { FaCalendar, FaStar, FaTrash } from "react-icons/fa";

const TMDB_POSTER_BASE_URL = 'https://image.tmdb.org/t/p/w500';

const HistoryPage = () => {
  const [history, setHistory] = useState([]);
  const [enrichedHistory, setEnrichedHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_SERVER_URI}/recommend/history`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const historyData = response.data.history || [];
      setHistory(historyData);

      // Enrich history with movie details from TMDB
      await enrichHistoryWithMovieDetails(historyData);
    } catch (err) {
      console.error("Error fetching history:", err);
      setError("Failed to load recommendation history");
    } finally {
      setLoading(false);
    }
  };

  const enrichHistoryWithMovieDetails = async (historyData) => {
    const enrichedData = await Promise.all(
      historyData.map(async (session) => {
        try {
          // Fetch details for input movies
          const inputMoviesDetails = await fetchMovieDetails(session.inputMovies);
          // Fetch details for output movies
          const outputMoviesDetails = await fetchMovieDetails(session.outputMovies);

          return {
            ...session,
            inputMoviesDetails,
            outputMoviesDetails
          };
        } catch (error) {
          console.error(`Error enriching session ${session.id}:`, error);
          return {
            ...session,
            inputMoviesDetails: [],
            outputMoviesDetails: []
          };
        }
      })
    );

    setEnrichedHistory(enrichedData);
  };

  const fetchMovieDetails = async (tmdbIds) => {
    const movieDetailsPromises = tmdbIds.map(async (tmdbId) => {
      try {
        const token = localStorage.getItem('accessToken');
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_SERVER_URI}/search/tmdb/${tmdbId}`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );

        return {
          tmdbId: response.data.tmdbId || tmdbId,
          title: response.data.title,
          year: response.data.year,
          posterPath: response.data.posterPath,
          overview: response.data.overview,
          voteAverage: response.data.voteAverage
        };
      } catch (error) {
        console.error(`Error fetching TMDB movie ${tmdbId}:`, error.message);
        return null;
      }
    });

    const movies = await Promise.all(movieDetailsPromises);
    return movies.filter(Boolean);
  };

  

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Loading your recommendation history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchHistory}
            className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Recommendation History
          </h1>
          <p className="text-gray-600">
            View your past movie recommendations grouped by session
          </p>
        </div>

        {/* History List */}
        {enrichedHistory.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg mb-4">No recommendation history yet</p>
            <p className="text-gray-400 mb-6">Get started by requesting your first recommendation!</p>
            <a
              href="/recommend"
              className="inline-block px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-all"
            >
              Get Recommendations
            </a>
          </div>
        ) : (
          <div className="space-y-8">
            {enrichedHistory.map((session) => (
              <div
                key={session.id}
                className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm"
              >
                {/* Session Header */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FaCalendar className="text-sm" />
                    <span className="text-sm">{formatDate(session.createdAt)}</span>
                  </div>
                  
                </div>

                {/* Input Movies */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Based on these movies:
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {session.inputMoviesDetails.map((movie) => (
                      <div
                        key={movie.tmdbId}
                        className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden"
                      >
                        <div className="relative aspect-[2/3]">
                          {movie.posterPath ? (
                            <img
                              src={`${TMDB_POSTER_BASE_URL}${movie.posterPath}`}
                              alt={movie.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">No Image</span>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <h4 className="text-xs font-semibold text-gray-900 line-clamp-2">
                            {movie.title}
                          </h4>
                          <p className="text-xs text-gray-600">{movie.year}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Output Movies */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    Recommended movies:
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {session.outputMoviesDetails.map((movie) => (
                      <div
                        key={movie.tmdbId}
                        className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-all"
                      >
                        <div className="relative aspect-[2/3]">
                          {movie.posterPath ? (
                            <img
                              src={`${TMDB_POSTER_BASE_URL}${movie.posterPath}`}
                              alt={movie.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                              <span className="text-gray-400 text-xs">No Image</span>
                            </div>
                          )}
                        </div>
                        <div className="p-2">
                          <h4 className="text-xs font-semibold text-gray-900 line-clamp-2 mb-1">
                            {movie.title}
                          </h4>
                          <p className="text-xs text-gray-600 mb-1">{movie.year}</p>
                          {movie.voteAverage && (
                            <div className="flex items-center gap-1 text-xs text-gray-600">
                              <FaStar className="text-yellow-500 text-xs" />
                              <span>{movie.voteAverage.toFixed(1)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryPage;
